<template>
  <transition name="img-viewr-fade">
    <div
      v-show="showImg"
      ref="imgView"
      :style="`z-index: ${zIndex}`"
      class="img-viewr__wrapper theme-QDS-dark"
    >
      <div class="img-viewr__mask" @click.self="handleMaskClick" />
      <!-- ARROW -->
      <template v-if="!isSingle">
        <span
          :class="{ 'is-disabled': !infinite && isFirst }"
          class="img-viewr__btn img-viewr__prev"
          @click="prev"
        >
        </span>
        <span
          :class="{ 'is-disabled': !infinite && isLast }"
          class="img-viewr__btn img-viewr__next"
          @click="next"
        >
        </span>
      </template>
      <footer class="img-viewr__footer">
        <!-- ACTIONS -->
        <div class="img-viewr__actions">
          <div
            class="img-viewr__btn zoomOut img-viewr__actions__inner"
            @click="handleActions('zoomOut')"
          ></div>
          <div
            class="img-viewr__btn zoomIn img-viewr__actions__inner"
            @click="handleActions('zoomIn')"
          ></div>
          <div class="img-viewr__actions__splitor" />
          <div
            class="img-viewr__btn rotate_left img-viewr__btn__left img-viewr__actions__inner"
            @click="handleActions('rotateL')"
          ></div>
          <div
            class="img-viewr__btn rotate_right img-viewr__actions__inner"
            @click="handleActions('rotateR')"
          ></div>
          <div class="img-viewr__actions__splitor" />
          <div class="img-viewr__indicator">
            {{ index + 1 }}/{{ urls.length }}
          </div>
        </div>
        <!-- CLOSE -->
        <span class="img-viewr__btn img-viewr__close" @click="hide">
          退出预览
        </span>
      </footer>
      <!-- CANVAS -->
      <div class="img-viewr__canvas">
        <img
          ref="imageRef"
          :src="currentImg"
          :style="imgStyle"
          @load="handleImgLoaded"
          @error="handleImgError"
        />
      </div>
    </div>
  </transition>
</template>
<script>
import { rafThrottle } from '../utils/utils.js';
import { on, off } from '../utils/dom.js';
/**
 * 这是一个图片预览组件
 * @type component
 * @title 图片预览
 * @category 数据展示
 */
export default {
  name: 'PictureViewer',
  props: {
    /**
     * 预览弹出双向绑定的value
     * @group 数据
     * @title 预览弹出双向绑定的value
     * @values true|false
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * 预览图片的url列表
     * @group 数据
     * @title 预览图片的url列表
     * @item {String} url地址
     */
    urls: {
      type: Array,
      default: () => [],
    },
    /**
     * 蒙层zIndex
     * @group 数据
     * @title 蒙层zIndex
     */
    zIndex: {
      type: Number,
      default: 3000,
    },
    /**
     * 初始打开时，需要显示的图片的下标
     * @group 数据
     * @title 初始打开时，需要显示的图片的下标
     */
    initialIndex: {
      type: Number,
      default: 0,
    },
    /**
     * 初始打开时，图片的宽度
     * @group 数据
     * @title 初始打开时，图片的宽度
     */
    initialWidth: {
      type: String,
      default: '',
    },
    /**
     * 初始打开时，图片的高度
     * @group 数据
     * @title 初始打开时，图片的高度
     */
    initialHeight: {
      type: String,
      default: '',
    },
    /**
     * 点击蒙层是否关闭
     * @group 功能
     * @title 点击蒙层是否关闭
     * @values true|false
     */
    closeOnClickMask: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否可以循环展示
     * @group 功能
     * @title 是否可以循环展示
     * @values true|false
     */
    infinite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      index: this.initialIndex,
      loading: false,
      showImg: false,
      transform: {
        scale: 1,
        deg: 0,
        offsetX: 0,
        offsetY: 0,
        enableTransition: false,
      },
      isMobile: !this.$platform.pc,
      dragHandler: null,
      containerDom: null,
      image: null,

      initialScale: 1,
      currentScale: 1,
      startX: 0,
      startY: 0,

      isDragging: false,
      translateX: 0,
      translateY: 0,
    };
  },
  computed: {
    isSingle() {
      return this.urls.length <= 1;
    },
    isFirst() {
      return this.index === 0;
    },
    isLast() {
      return this.index === this.urls.length - 1;
    },
    currentImg() {
      const image = this.urls[this.index];
      return image || this.urls[0];
    },
    imgStyle() {
      const { scale, deg, offsetX, offsetY, enableTransition } = this.transform;
      const style = {
        transform: `scale(${scale}) rotate(${deg}deg)`,
        transition: enableTransition
          ? 'all -0.25s cubic-bezier(0.36, 0.01, 0.1, 1);'
          : '',
        'margin-left': offsetX < 0 ? `${offsetX}px` : '0px',
        'margin-right': offsetX > 0 ? `${-offsetX}px` : '0px',
        'margin-top': `${offsetY}px`,
        maxHeight: '100%',
        maxWidth: '100%',
        width: this.initialWidth || undefined,
        height: this.initialHeight || undefined,
      };
      return style;
    },
  },
  watch: {
    value: {
      handler(val) {
        this.showImg = val;
      },
      immediate: true,
    },
    initialIndex(val) {
      if (val !== this.index) {
        this.index = val;
      }
    },
    index(val) {
      this.reset();
      /**
       * 图片切换时触发
       * @title 图片切换时触发
       * @arg {Number} 当前图片的索引
       */
      this.$emit('switch', val);
    },
    currentImg() {
      this.$nextTick(() => {
        const $img = this.$refs.imageRef;
        if ($img) {
          if (!$img.complete) {
            this.loading = true;
          }
        } else {
          this.loading = true;
        }
      });
    },
  },
  mounted() {
    this.$el.addEventListener(
      'touchmove',
      (e) => {
        if (e.scale !== 1) {
          e.preventDefault();
        };
      },
      { passive: false },
    );

    this.containerDom = this.$refs.imgView;
    this.image = this.$refs.imageRef;

    if (this.isMobile) {
      on(this.containerDom, 'touchstart', this.handleTouchStart);
      on(this.containerDom, 'touchmove', this.handleTouchMove);
      on(this.containerDom, 'touchend', this.handleTouchEnd);
    } else {
      on(this.image, 'mousedown', this.handleMouseDown);
      on(this.containerDom, 'mousewheel', this.zoomWithMouseWheel);
      on(this.containerDom, 'DOMMouseScroll', this.zoomWithMouseWheel);
    };

    this.$nextTick(() => {
      document.addEventListener('keyup', this.hide);
    });
  },
  beforeDestroy() {
    if (this.isMobile) {
      off(this.containerDom, 'touchstart', this.handleTouchStart);
      off(this.containerDom, 'touchmove', this.handleTouchMove);
      off(this.containerDom, 'touchend', this.handleTouchEnd);
    } else {
      off(this.image, 'mousedown', this.handleMouseDown);
      off(this.containerDom, 'mousewheel', this.zoomWithMouseWheel);
      off(this.containerDom, 'DOMMouseScroll', this.zoomWithMouseWheel);
    };
    document.removeEventListener('keyup', this.hide);
    this.$el.removeEventListener(
      'touchmove',
      (e) => {
        if (e.scale !== 1) {
          e.preventDefault();
        };
      },
      { passive: false },
    );
  },
  methods: {
    /**
     * @param {Object} e e
     * @private
     */
    zoomWithMouseWheel(e) {
      let action = '';
      const actualEvent = e || window.event;
      if (actualEvent.cancelable) {
        // 判断默认行为是否已经被禁用
        if (!actualEvent.defaultPrevented) {
          actualEvent.preventDefault();
        };
      };
      if (actualEvent.wheelDelta) {
        // 判断浏览器IE，谷歌滑轮事件
        action = actualEvent.wheelDelta > 0 ? 'zoomIn' : 'zoomOut';
      } else if (actualEvent.detail) {
        // Firefox滑轮事件
        action = actualEvent.detail > 0 ? 'zoomIn' : 'zoomOut';
      };
      this.handleActions(action);
    },
    /**
     * @private
     */
    handleMaskClick() {
      if (this.closeOnClickMask) {
        this.hide();
      };
    },
    /**
     * @private
     */
    hide() {
      this.showImg = false;
      /**
       * 隐藏时触发
       * @title 隐藏时触发
       * @args {boolean} param 固定返回false
       */
      this.$emit('input', false);
      /**
       * 隐藏时触发
       * @title 隐藏时触发
       */
      this.$emit('close');
    },
    /**
     * @private
     */
    handleImgLoaded() {
      this.loading = false;
    },
    /**
     * @private
     */
    handleImgError() {
      this.loading = false;
      if (this.$refs.imageRef) {
        this.$refs.imageRef.alt = '加载失败';
      };
    },
    /**
     * @private
     */
    reset() {
      this.transform.scale = 1;
      this.transform.deg = 0;
      this.transform.offsetX = 0;
      this.transform.offsetY = 0;
      this.transform.enableTransition = false;
    },
    /**
     * @private
     */
    prev() {
      if (this.isFirst && !this.infinite) return;
      const len = this.urls.length;
      this.index = (this.index - 1 + len) % len;
    },
    /**
     * @private
     */
    next() {
      if (this.isLast && !this.infinite) return;
      const len = this.urls.length;
      this.index = (this.index + 1) % len;
    },
    /**
     * @param {Object} action action
     * @param {Object} options options
     * @private
     */
    handleActions(action, options = {}) {
      if (this.loading) return;
      const { zoomRate, enableTransition } = {
        zoomRate: 0.2,
        enableTransition: true,
        ...options,
      };
      switch (action) {
        case 'zoomOut':
          if (this.transform.scale > 0.2) {
            this.transform.scale = parseFloat((this.transform.scale - zoomRate).toFixed(3));
          };
          break;
        case 'zoomIn':
          this.transform.scale = parseFloat((this.transform.scale + zoomRate).toFixed(3));
          break;
        case 'rotateL':
          this.transform.deg = this.transform.deg - 90;
          break;
        case 'rotateR':
          this.transform.deg = this.transform.deg + 90;
          break;
      };
      this.transform.enableTransition = enableTransition;
    },
    /**
     * @param {Object} e e
     * @private
     */
    handleMouseDown(e) {
      e.preventDefault();
      if (this.loading || e.button !== 0) return;

      const { offsetX, offsetY } = this.transform;
      const startX = e.pageX;
      const startY = e.pageY;
      this.dragHandler = rafThrottle((ev) => {
        this.transform.offsetX = offsetX + ev.pageX - startX;
        this.transform.offsetY = offsetY + ev.pageY - startY;
      });
      document.addEventListener('mousemove', this.dragHandler);
      document.addEventListener('mouseup', this.handleMouseUp);
    },
    /**
     * @private
     */
    handleMouseUp() {
      if (this.dragHandler) {
        document.removeEventListener('mousemove', this.dragHandler);
      }
    },
    handleTouchStart(e) {
      const { touches } = e;
      if (touches && touches.length === 2) {
        this.startX = Math.abs(touches[0].clientX - touches[1].clientX);
        this.startY = Math.abs(touches[0].clientY - touches[1].clientY);
        this.initialScale = this.currentScale;
      } else if (touches && touches.length === 1) {
        this.startX = touches[0].clientX;
        this.startY = touches[0].clientY;
        this.isDragging = true;
      };
    },
    handleTouchMove(e) {
      e.preventDefault();
      const { touches } = e;
      if (touches && touches.length === 2) {
        const moveX = Math.abs(touches[0].clientX - touches[1].clientX);
        const moveY = Math.abs(touches[0].clientY - touches[1].clientY);
        const distance = Math.sqrt(moveX ** 2 + moveY ** 2);
        this.currentScale = this.initialScale * (distance / Math.sqrt(this.startX ** 2 + this.startY ** 2));
        this.image.style.transform = `scale(${this.currentScale})`;
        this.transform.scale = this.currentScale;
      } else if (touches && touches.length === 1 && this.isDragging) {
        const moveX = touches[0].clientX - this.startX;
        const moveY = touches[0].clientY - this.startY;

        this.translateX += moveX;
        this.translateY += moveY;

        this.transform.offsetX = this.translateX;
        this.transform.offsetY = this.translateY;

        this.startX = touches[0].clientX;
        this.startY = touches[0].clientY;
      };
    },
    handleTouchEnd() {
      // 重置参数
      this.initialScale = this.currentScale;
      this.isDragging = false;
    },
  },
};
</script>

<style scoped>
.img-viewr-fade-enter-active {
  -webkit-animation: img-viewr-fade-in 0.3s;
  animation: img-viewr-fade-in 0.3s;
}

.img-viewr-fade-leave-active {
  -webkit-animation: img-viewr-fade-out 0.3s;
  animation: img-viewr-fade-out 0.3s;
}

@-webkit-keyframes img-viewr-fade-in {
  0% {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes img-viewr-fade-in {
  0% {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@-webkit-keyframes img-viewr-fade-out {
  0% {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes img-viewr-fade-out {
  0% {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.img-viewr__wrapper {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.img-viewr__wrapper .is-disabled {
  opacity: 0.38;
  pointer-events: none;
}

.img-viewr__footer {
  bottom: 25px;
  left: 50%;
  z-index: 2;
  -webkit-transform: translateX(-50%);
  -ms-transform: translateX(-50%);
  transform: translateX(-50%);
}

.img-viewr__btn,
.img-viewr__footer {
  position: absolute;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
}

.img-viewr__btn {
  z-index: 1;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  background-color: rgba(38, 45, 61, 0.8);
}

.img-viewr__close {
  position: static;
  width: 90px;
  height: 40px;
  border-radius: 20px;
  color: #fff;
  font-size: 14px;
  margin-left: 24px;
}

.img-viewr__canvas {
  width: 100%;
  height: 100%;
}

.img-viewr__actions,
.img-viewr__canvas {
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
}

.img-viewr__actions {
  height: 40px;
  border-radius: 20px;
  background-color: rgba(38, 45, 61, 0.8);
  overflow: hidden;
}

.img-viewr__actions__inner {
  background-color: transparent;
  position: static;
  padding-left: 18px;
  height: 100%;
  cursor: default;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  border-radius: 0;
  color: #fff;
  cursor: pointer;
}

.img-viewr__actions__splitor {
  width: 1px;
  height: 16px;
  background-color: rgba(0, 0, 0, 0.2);
  margin: 0 12px;
}

.img-viewr__btn__left {
  padding-left: unset;
}

.img-viewr__next,
.img-viewr__prev {
  top: 50%;
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  color: #fff;
  cursor: pointer;
}

.img-viewr__prev {
  left: 24px;
}

.img-viewr__next {
  right: 24px;
}

.img-viewr__next:before,
.img-viewr__prev:before,
.img-viewr__actions__inner:before {
  content: '';
  display: block;
  width: 16px;
  height: 16px;
  background-size: cover;
  background-repeat: no-repeat;
}

.img-viewr__prev:before {
  background-image: url('../assets/prev.svg');
}

.img-viewr__next:before {
  background-image: url('../assets/next.svg');
}

.zoomOut:before {
  background-image: url('../assets/zoomOut.svg');
}

.zoomIn:before {
  background-image: url('../assets/zoomIn.svg');
}

.rotate_left:before {
  background-image: url('../assets/rotateLeft.svg');
}

.rotate_right:before {
  background-image: url('../assets/rotateRight.svg');
}

.rotate_left {
  padding-left: 0;
}

.img-viewr__mask {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.4);
}

.img-viewr__indicator {
  color: #fff;
  font-size: 14px;
  text-align: center;
  margin-right: 16px;
}
</style>
